import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { SyncStatusIndicator } from "@/components/sync/SyncStatusIndicator";
import { PageTransition } from "@/components/common/PageTransition";
import {
  OpenSessionModal,
  CloseSessionModal,
  SwitchWarningModal,
} from "@/components/billing-session/SessionModals";

import { useDashboardData } from "./hooks/useDashboardData";
import {
  Sidebar,
  HeaderSearchBar,
  StoreCounterSelector,
  NotificationsDropdown,
  UserProfileDropdown,
  ThemeToggle,
} from "./components";

export function DashboardLayout() {
  const location = useLocation();
  const isPOSRoute = location.pathname.startsWith("/pos");
  const data = useDashboardData();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  useEffect(() => {
    // Always start POS with collapsed nav for extra workspace.
    // Users can still manually expand it.
    if (isPOSRoute) {
      setDesktopSidebarCollapsed(true);
    }
  }, [isPOSRoute]);

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex font-sans">
      {/* Session Modals */}
      <OpenSessionModal
        isOpen={data.showOpenSessionModal}
        onClose={() => data.setShowOpenSessionModal(false)}
        storeId={data.selectedStoreId || ""}
        storeName={data.currentStoreName}
        onSessionOpened={(storeId, counterId) =>
          data.fetchActiveSession(storeId, counterId)
        }
      />
      <CloseSessionModal
        isOpen={data.showCloseSessionModal}
        onClose={() => data.setShowCloseSessionModal(false)}
        onSessionClosed={data.handleSessionClosed}
      />
      <SwitchWarningModal
        isOpen={data.showSwitchWarningModal}
        onClose={() => {
          data.setShowSwitchWarningModal(false);
          data.setPendingSwitch(null);
        }}
        onConfirmClose={() => {
          data.setShowSwitchWarningModal(false);
          data.setShowCloseSessionModal(true);
        }}
        switchType={data.pendingSwitch?.type || "counter"}
        targetName={data.getSwitchTargetName()}
      />

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        desktopSidebarCollapsed={desktopSidebarCollapsed}
        setDesktopSidebarCollapsed={setDesktopSidebarCollapsed}
        navigationGroups={data.navigationGroups}
        enabledModules={data.user?.enabledModules}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden bg-gray-50/50">
        <header className="sticky top-0 z-[60] flex h-[72px] shrink-0 items-center gap-3 border-b border-gray-200/80 bg-white px-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-300 sm:px-4 lg:px-5">
          {/* Left Side: Mobile Menu & Search */}
          <div className="flex min-w-0 flex-1 items-center gap-3 xl:max-w-[520px]">
            <button
              onClick={() => setSidebarOpen(true)}
              type="button"
              aria-label="Open navigation"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 text-gray-600 transition-colors hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <HeaderSearchBar />
          </div>

          {/* Center: Store & Counter Selector */}
          <StoreCounterSelector
            isBusinessOwner={data.isBusinessOwner}
            availableStores={data.availableStores}
            selectedStoreId={data.selectedStoreId}
            handleStoreChange={data.handleStoreChange}
            counters={data.counters}
            selectedCounterId={data.selectedCounterId}
            handleCounterChange={data.handleCounterChange}
            activeSession={data.activeSession}
            onOpenSession={() => data.setShowOpenSessionModal(true)}
            onCloseSession={() => data.setShowCloseSessionModal(true)}
          />

          {/* Right Side: Actions & Profile */}
          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-1.5">
            <SyncStatusIndicator />
            <div className="hidden sm:block">
              <ThemeToggle />
            </div>

            <NotificationsDropdown
              isOpen={notificationsOpen}
              onToggle={() => {
                setNotificationsOpen(!notificationsOpen);
                setProfileOpen(false);
              }}
              onClose={() => setNotificationsOpen(false)}
            />

            <UserProfileDropdown
              user={data.user}
              isOpen={profileOpen}
              onToggle={() => {
                setProfileOpen(!profileOpen);
                setNotificationsOpen(false);
              }}
              onClose={() => setProfileOpen(false)}
              onLogout={data.handleLogout}
            />
          </div>
        </header>

        {data.user?.impersonation && (
          <div className="flex shrink-0 items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-center text-xs font-semibold text-amber-950">
            Support impersonation is active. This session is audited, revocable,
            and expires at{" "}
            {new Date(data.user.impersonation.expiresAt).toLocaleTimeString()}.
          </div>
        )}

        {/* Page Content */}
        <main
          className={`flex-1 min-h-0 ${isPOSRoute ? "overflow-hidden p-0" : "overflow-y-auto p-8"}`}
        >
          <PageTransition className="h-full min-h-0">
            <Outlet />
          </PageTransition>
        </main>
      </div>
    </div>
  );
}
